import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../components/config/api';
import { useSocket } from './useSocket';
import {
  DAILY_DISHES_OFF, DAILY_DISHES_SOCKET_EVENT, DAILY_DISHES_WINDOW_EVENT, type DailyDishesToday,
} from '../utils/dailyDishes';

// Πολλά πατήματα στη σειρά (ο ταμίας τσεκάρει 6 πιάτα) = μία ανανέωση ανά ταμείο, όχι έξι.
const DEBOUNCE_MS = 400;

const parse = (data: any): DailyDishesToday => (data && data.enabled === true && Number.isInteger(data.categoryId)
  ? {
    enabled: true,
    categoryId: data.categoryId,
    productIds: Array.isArray(data.productIds) ? data.productIds.filter((id: unknown) => Number.isInteger(id)) : [],
  }
  : DAILY_DISHES_OFF);

/**
 * Τα σημερινά «Πιάτα ημέρας» του καταστήματος για τους καταλόγους (Take Away, τραπέζια, delivery, PDA).
 *
 * Ρωτά τον server ΜΟΝΟ όταν το κατάστημα έχει ανοιχτή τη λειτουργία (store.daily_dishes_enabled) — κανένα
 * επιπλέον αίτημα στα υπόλοιπα καταστήματα — ή όταν φτάσει ειδοποίηση αλλαγής (socket 'daily-dishes:changed'
 * από άλλο ταμείο/τη διαχείριση, ή το ίδιο γεγονός μέσα στην καρτέλα). Τότε καλεί και το `onChange`, ώστε η
 * οθόνη να ξαναφέρει τα προϊόντα (νέο πιάτο, νέα τιμή, ή — στο Take Away — τα σημερινά από τον server).
 * Σφάλμα / παλιός server = κλειστό: ο κατάλογος μένει όπως πριν.
 */
export function useDailyDishesToday(storeId: number | undefined, enabledHint: boolean, onChange?: () => void) {
  const { socket } = useSocket();
  const [today, setToday] = useState<DailyDishesToday>(DAILY_DISHES_OFF);
  const seq = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const reload = useCallback(async () => {
    if (!storeId) return;
    const id = ++seq.current;
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/daily-dishes/today`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('xentrix_token')}` },
      });
      if (!res || !res.ok) {
        // 404 = παλιός server χωρίς τη λειτουργία· 5xx = κρατάμε ό,τι ξέραμε.
        if (res && res.status === 404 && id === seq.current) setToday(DAILY_DISHES_OFF);
        return;
      }
      const data = await res.json();
      if (id === seq.current) setToday(parse(data));
    } catch {
      /* χωρίς δίκτυο: κρατάμε ό,τι είχαμε */
    }
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return;
    if (enabledHint) reload();
    else {
      seq.current += 1;
      setToday(DAILY_DISHES_OFF);
    }
  }, [storeId, enabledHint, reload]);

  // Ειδοποιήσεις: ένα χρονόμετρο για όλες, ώστε μια σειρά από αλλαγές να φέρει μία ανανέωση.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const changed = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      reload();
      onChangeRef.current?.();
    }, DEBOUNCE_MS);
  }, [reload]);
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!socket || !storeId) return undefined;
    socket.on(DAILY_DISHES_SOCKET_EVENT, changed);
    return () => { socket.off(DAILY_DISHES_SOCKET_EVENT, changed); };
  }, [socket, storeId, changed]);

  useEffect(() => {
    if (!storeId) return undefined;
    window.addEventListener(DAILY_DISHES_WINDOW_EVENT, changed);
    return () => window.removeEventListener(DAILY_DISHES_WINDOW_EVENT, changed);
  }, [storeId, changed]);

  return { today, reload };
}
